import {useCallback, useEffect, useState} from "react";
import {
  GenderEnum,
  IndustryEnum,
  MemberOfPadelClubEnum,
  NprpRatingEnum,
  PlayingDurationEnum,
  PlayingFrequencyEnum,
  SkillLevelEnum,
  UserProfileModel, UserProfileModelForSend,
  UserRequests,
  YesOrNotEnum
} from "@/app/api/user";
import {useNavigate, useLocation} from "react-router-dom";
import {route} from "@/app/routes/AppRoutes";
import {toSnakeUpperCase} from "@/app/utils/custom_function";
import { logEventSafe } from "@/app/utils/analytics";

export enum OnboardingStep {
  FIRST_THINGS_FIRST = 0,
  SKIP = 1,
  EXPERIENCE = 2,
  HAS_NPRP_RATING = 3,
  SKILL_OR_RATING = 4,
  CITY = 5,
  GENDER = 6,
  DATE_OF_BIRTH = 7,
  MEMBERSHIP = 8,
  LONG_TERM = 9,
  OFTEN = 10,
  ABOUT = 11,
  INDUSTRY = 12,
  SOCIAL_LINKS = 13,
}

export namespace OnboardingStep {
  export const values = Object.values(OnboardingStep)
    .filter(v => typeof v === 'number') as OnboardingStep[];
}

interface ScreenState {
  hasLoading: boolean;
  hasError?: string;
  currentPage: number;
  isFinished: boolean;
  hasNPRPRating: boolean;
}

const emptyProfile: UserProfileModel = {
  username: "",
  ntrpRating: undefined,
  skillLevel: undefined,
  city: undefined,
  gender: undefined,
  dateOfBirth: undefined,
  padelClubMembership: undefined,
  playingDuration: undefined,
  playingFrequency: undefined,
  aboutYourself: "",
  industry: "",
  instagram: "",
  linkedin: "",
  rocketSportsExperience: undefined,
  imageLink: "",
  showGenderInProfile: false,
  showDateOfBirthInProfile: false,
};

const useOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const stepParam = searchParams.get("step");
  const parsedStep = stepParam !== null ? Number(stepParam) : 0;
  const initialStep = OnboardingStep.values.includes(parsedStep as OnboardingStep)
    ? (parsedStep as OnboardingStep)
    : OnboardingStep.FIRST_THINGS_FIRST;

  const [state, setState] = useState<ScreenState>({
    hasLoading: false,
    hasError: undefined,
    currentPage: initialStep,
    isFinished: false,
    hasNPRPRating: false,
  });

  const [profile, setProfile] = useState<UserProfileModel>(emptyProfile);

  useEffect(() => {
    UserRequests.getCurrentUser().then((user) => {
      setProfile({
        ...emptyProfile,
        username: user.user?.username,
        imageLink: user.user?.imageLink,
        ntrpRating: user.ntrpRating,
        skillLevel: user.skillLevel,
        city: user.city,
        gender: user.gender,
        dateOfBirth: user.dateOfBirth,
        padelClubMembership: user.padelClubMembership,
        playingDuration: user.playingDuration,
        playingFrequency: user.user.playFrequency ?? undefined,
        aboutYourself: user.aboutYourself ?? "",
        industry: user.industry ?? "",
        instagram: user.instagram ?? "",
        linkedin: user.linkedin ?? "",
        rocketSportsExperience: user.rocketSportsExperience,
        showGenderInProfile: user.showGenderInProfile,
      });
    });
  }, []);

  useEffect(() => {
    if (state.isFinished) navigate(route.home);
  }, [state.isFinished, navigate]);

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const onContinue = useCallback(async () => {
    if (state.currentPage === OnboardingStep.values.length - 1) {
      await saveUserProfile();
      // Log onboarding finished
      await logEventSafe('padel_onboard_finished', {
        user_city: profile.city,
        skill_level: profile.skillLevel,
        preferences: JSON.stringify({
          padelClubMembership: profile.padelClubMembership,
          playingFrequency: profile.playingFrequency,
          industry: profile.industry,
          rocketSportsExperience: profile.rocketSportsExperience,
        })
      });
      updateState({isFinished: true});
    } else {
      updateState({currentPage: state.currentPage + 1});
    }
  }, [state.currentPage, updateState, profile]);

  const skip = useCallback(async () => {
    if (state.currentPage === OnboardingStep.values.length - 1) {
      await saveUserProfile();
      await logEventSafe('padel_onboard_finished', {
        user_city: profile.city,
        skill_level: profile.skillLevel,
        preferences: JSON.stringify({
          padelClubMembership: profile.padelClubMembership,
          playingFrequency: profile.playingFrequency,
          industry: profile.industry,
          rocketSportsExperience: profile.rocketSportsExperience,
        })
      });
      updateState({isFinished: true});
    } else {
      updateState({currentPage: state.currentPage + 1});
    }
  }, [state.currentPage, updateState, profile]);

  const back = useCallback(() => {
    updateState({currentPage: Math.max(0, state.currentPage - 1)});
  }, [state.currentPage, updateState]);

  const setName = useCallback((name: string) => {
    if (!name || profile.username === name) return;
    setProfile(prev => ({...prev, username: name}));
  }, [profile.username]);

  const dataURLtoFile = (dataUrl: string, fileName = 'file.png'): File => {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)![1];
    const binary = atob(base64);
    const len = binary.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = binary.charCodeAt(i);
    }
    return new File([arr], fileName, {type: mime});
  }

  const uploadImage = async (dataUrl: string): Promise<string | undefined> => {
    try {
      const file = dataURLtoFile(dataUrl, 'upload.png');
      const formData = new FormData();
      formData.append('file', file);
      return await UserRequests.uploadImage(formData);
    } catch (err) {
      console.error(err);
      return undefined;
    }
  };

  const setImage = useCallback(async (data: string) => {
    const imageLink = await uploadImage(data);
    setProfile(prev => ({...prev, imageLink}));
  }, []);

  const setExperience = useCallback((value: YesOrNotEnum) => {
    setProfile(prev => ({...prev, rocketSportsExperience: value}));
  }, []);

  const setHasNPRPRating = useCallback((value: string) => {
    const hasRating = value === YesOrNotEnum.YES;
    updateState({hasNPRPRating: hasRating});
  }, [updateState]);

  const setRating = useCallback((value: NprpRatingEnum) => {
    setProfile(prev => ({...prev, ntrpRating: value}));
  }, []);

  const setSkill = useCallback((value: SkillLevelEnum) => {
    setProfile(prev => ({...prev, skillLevel: value}));
  }, []);

  const setCity = useCallback((value: string) => {
    setProfile(prev => ({...prev, city: value}));
  }, []);

  const setGender = useCallback((value: GenderEnum) => {
    setProfile(prev => ({...prev, gender: value}));
  }, []);

  const toggleShowInProfileGender = useCallback(() => {
    setProfile(prev => ({
      ...prev,
      showGenderInProfile: !prev.showGenderInProfile
    }));
  }, []);

  const setDateOfBirth = useCallback((date: Date) => {
    setProfile(prev => ({
      ...prev,
      dateOfBirth: date.toISOString().slice(0, 10)
    }));
  }, []);

  const toggleShowInProfileDateOfBirth = useCallback(() => {
    setProfile(prev => ({
      ...prev,
      showDateOfBirthInProfile: !prev.showDateOfBirthInProfile
    }));
  }, []);

  const setMembership = useCallback((value: MemberOfPadelClubEnum) => {
    setProfile(prev => ({...prev, padelClubMembership: value}));
  }, []);

  const setLongTerm = useCallback((value: PlayingDurationEnum) => {
    setProfile(prev => ({...prev, playingDuration: value}));
  }, []);

  const setOften = useCallback((value: PlayingFrequencyEnum) => {
    setProfile(prev => ({...prev, playingFrequency: value}));
  }, []);

  const setAbout = useCallback((about: string) => {
    setProfile(prev => ({...prev, aboutYourself: about}));
  }, []);

  const setIndustry = useCallback((industries: IndustryEnum[]) => {
    setProfile(prev => ({...prev, industry: industries.join(",")}));
  }, []);

  const setInstagramLink = useCallback((link: string) => {
    setProfile(prev => ({...prev, instagram: link}));
  }, []);

  const setLinkedinLink = useCallback((link: string) => {
    setProfile(prev => ({...prev, linkedin: link}));
  }, []);

  const saveUserProfile = useCallback(async () => {
    try {
      const forsSend: UserProfileModelForSend = {
        ...profile,
        playingFrequency: toSnakeUpperCase(profile.playingFrequency ?? "")
      };
      await UserRequests.updateUser(forsSend);
      await UserRequests.getCurrentUser();
    } catch (e) {
      console.error(e);
    }
  }, [profile]);

  const calculateDisable = useCallback((page?: number) => {
    const stepsValidation: Record<number, () => boolean> = {
      0: () => (profile.username ?? "").length <= 0,
      1: () => false,
      2: () => profile.rocketSportsExperience === undefined,
      3: () => state.hasNPRPRating === undefined,
      4: () => state.hasNPRPRating ? profile.ntrpRating === undefined : profile.skillLevel === undefined,
      5: () => profile.city === undefined,
      6: () => profile.gender === undefined,
      7: () => profile.dateOfBirth === undefined,
      8: () => profile.padelClubMembership === undefined,
      9: () => profile.playingDuration === undefined,
      10: () => profile.playingFrequency === undefined,
      11: () => (profile.aboutYourself ?? "").length <= 10,
      12: () => (profile.industry ?? "").length === 0,
      13: () => (profile.instagram ?? "").length <= 2 && (profile.linkedin ?? "").length <= 2,
    }
    return (stepsValidation[page ?? state.currentPage] ?? (() => true))();
  }, [profile, state.currentPage, state.hasNPRPRating]);

  return {
    state,
    profile,
    onContinue,
    skip,
    back,
    calculateDisable,
    setName,
    setImage,
    setExperience,
    setHasNPRPRating,
    setRating,
    setSkill,
    setCity,
    setGender,
    toggleShowInProfileGender,
    setDateOfBirth,
    toggleShowInProfileDateOfBirth,
    setMembership,
    setLongTerm,
    setOften,
    setAbout,
    setIndustry,
    setInstagramLink,
    setLinkedinLink,
  };
};

export default useOnboarding;